import React from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useRouter } from 'next/router'
import 'dayjs/locale/id'
import clsx from 'clsx'

import { Text } from '@wartek-id/text'
import { Shimmer } from '@wartek-id/shimmer'
import { Avatar } from '@wartek-id/avatar'

import { GATrackEvent } from 'utils/googleAnalytics'
import { Anchor } from 'components/Anchor'
import { cacheImage } from 'app/PelatihanMandiriPage/helper'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.locale('id')

export interface speakers {
  name: string
  avatar: string
  profesi: string
}

export interface Webinar {
  id: string
  title: string
  subtitle: string
  description: string
  startDate: string
  endDate: string
  isOpen: boolean
  links: {
    zoom: string
    youtube: string
    interaction: string
  }
  topic: {
    id: number
    title: string
  }
  module: {
    id: number
    title: string
    postTestRequired: boolean
  }[]
  speakers: speakers[]
}

export interface WebinarCardProps {
  id: string
  href: string
  title: string
  speakers: speakers[]
  startDate: string
  endDate: string
  topicId?: string
  topicName?: string
  active?: boolean
  className?: string
  isShowNameSpeakers?: boolean
  titleClassName?: string
}

export const WebinarPreloader = () => {
  return (
    <div className="relative rounded border border-subdued bg-white px-4 py-3">
      <Shimmer className="mb-2" height={14} style={{ width: '50%' }} />
      <Shimmer className="mb-4" height={16} style={{ width: '100%' }} />
      <Shimmer className="mb-4" height={14} style={{ width: '100%' }} />
      <div className="flex justify-between">
        <Shimmer className="mb-4" height={14} style={{ width: '30%' }} />
        <Shimmer className="mb-4" height={14} width={100} />
      </div>
    </div>
  )
}

const WebinarCard = ({
  id,
  href,
  topicName,
  topicId,
  title,
  speakers,
  startDate,
  endDate,
  active = false,
  className,
  isShowNameSpeakers = true,
  titleClassName,
}: WebinarCardProps) => {
  const { pathname } = useRouter()
  const speakersName = speakers.map((speaker) => speaker.name).join(', ')
  const startDateObj = dayjs(startDate)
  const endDateObj = dayjs(endDate)
  const eventDate = {
    day: startDateObj.format('D'),
    month: startDateObj.format('MMM'),
    time: `${startDateObj.tz('Asia/Jakarta').format('HH:mm')} - ${endDateObj
      .tz('Asia/Jakarta')
      .format('HH:mm')} WIB`,
  }

  const AvatarProfile = ({
    name,
    image,
    index,
  }: {
    name: string
    image: string
    index: number
  }) => {
    if (image) {
      return (
        <div
          style={{
            transform: `translate(-${(index + 1) * 3}px, 3px)`,
          }}
        >
          <img
            src={cacheImage(image)}
            alt={name}
            className="rounded-full bg-transparent h-9 w-9"
          />
        </div>
      )
    }

    return (
      <Avatar
        name={name}
        key={index}
        alt={name}
        src=""
        style={{
          transform: `translate(-${(index + 1) * 3}px,0)`,
        }}
      />
    )
  }

  return (
    <div
      data-testid="webinar"
      className={clsx(
        'relative rounded border border-subdued bg-white p-3',
        active && 'border-primary bg-blue-20',
        className
      )}
    >
      {!!topicName && (
        <Text variant="eyebrow-sm" color="subdued">
          {topicName}
        </Text>
      )}
      <Anchor
        href={href}
        onClick={() => {
          GATrackEvent({
            eventName: 'learning_activity',
            eventParams: {
              event_trigger: 'webinar_list',
              action_type:
                pathname ===
                `/${
                  process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                  'pelatihan-mandiri'
                }/webinar`
                  ? 'click_webinar_from_list'
                  : 'click_webinar_from_topic',
              meta: JSON.stringify({
                id: id,
                title: title,
                topik: topicName,
                topik_id: topicId,
              }),
            },
            sendDefaultParams: true,
          })
        }}
        className="overlay-link mb-2 block leading-none"
      >
        <Text as="span" variant="heading-sm" className={titleClassName}>
          {title}
        </Text>
      </Anchor>
      <div className="mb-3">
        {isShowNameSpeakers && (
          <Text
            as="div"
            variant="helper"
            color="subdued"
            className="leading-none"
          >
            {speakersName}
          </Text>
        )}
      </div>
      <div className="flex flex-wrap items-center">
        <div className="flex w-1/2 flex-shrink-0 items-center">
          <div className="z-10 flex h-9 w-9 items-center justify-center rounded-full border bg-white">
            <div className="py-1">
              <Text
                as="div"
                variant="body-sm"
                className="text-center leading-none"
              >
                <strong>{eventDate.day}</strong>
              </Text>
              <div className="-mt-1 text-[8px] font-semibold uppercase">
                {eventDate.month}
              </div>
            </div>
          </div>
          {speakers.map(({ name, avatar }, index) =>
            index < 3 ? (
              <AvatarProfile
                key={index + name}
                name={name}
                image={avatar}
                index={index}
              />
            ) : null
          )}
          {speakers.length > 3 ? (
            <div
              className="z-0 flex h-9 w-9 items-center justify-center rounded-full border bg-gray-20"
              style={{ transform: `translate(-15px,0)` }}
            >
              <Text variant="body-sm" className="text-subdued">
                +{speakers.length - 3}
              </Text>
            </div>
          ) : null}
        </div>
        <div className="w-1/2 flex-shrink-0 flex-grow-0 text-right">
          <Text variant="body-sm" color="subdued">
            <span className="font-semibold">{eventDate.time}</span>
          </Text>
        </div>
      </div>
    </div>
  )
}

export default WebinarCard
